<template>
  <div class="detail-page">
    <!-- Header -->
    <div class="page-header">
      <button
        class="btn-back"
        @click="$router.push({ name: 'DeliveryTripsPage' })"
      >
        ← Trips
      </button>
      <div v-if="trip" class="header-center">
        <h1 class="page-title">
          Trip · {{ formatDate(trip.createdAt) }}
          <span v-if="relativeLabel" class="relative-label">{{
            relativeLabel
          }}</span>
        </h1>
        <span class="status-badge" :class="statusClass(trip.status)">
          {{ statusLabel(trip.status) }}
        </span>
      </div>
      <div class="header-actions" v-if="trip">
        <button
          v-if="trip.status === 'draft'"
          class="btn-primary"
          @click="openAddOrders"
        >
          + Add Orders
        </button>
        <button
          v-if="trip.status === 'draft' && trip.orders && trip.orders.length"
          class="btn-success"
          @click="confirmStart"
          :disabled="acting"
        >
          ▶ Start Trip
        </button>
        <button
          v-if="trip.status === 'draft' || trip.status === 'in_progress'"
          class="btn-warning"
          @click="confirmOptimize"
          :disabled="acting || !hasGeocodedOrders"
          :title="
            !hasGeocodedOrders
              ? 'No orders with coordinates'
              : 'Optimize route with Google Maps'
          "
        >
          ⚡ Optimize Route
        </button>
        <button
          v-if="trip.status === 'in_progress'"
          class="btn-danger"
          @click="confirmComplete"
          :disabled="acting"
        >
          ✓ Complete Trip
        </button>
      </div>
    </div>

    <div v-if="loading" class="empty-state">Loading...</div>

    <template v-else-if="trip">
      <!-- Info bar -->
      <div class="info-bar">
        <div class="info-item">
          <span class="info-label">Driver</span>
          <span class="info-value">{{ trip.driverName || "—" }}</span>
        </div>
        <div class="info-item" v-if="trip.departamentoId">
          <span class="info-label">Department</span>
          <span class="info-value">{{ trip.departamentoId }}</span>
        </div>
        <div class="info-item" v-if="trip.warehouseId">
          <span class="info-label">Warehouse</span>
          <span class="info-value">{{ trip.warehouseId }}</span>
        </div>
        <div class="info-item" v-if="trip.startedAt">
          <span class="info-label">Started</span>
          <span class="info-value">{{ formatDate(trip.startedAt) }}</span>
        </div>
        <div class="info-item" v-if="trip.completedAt">
          <span class="info-label">Completed</span>
          <span class="info-value">{{ formatDate(trip.completedAt) }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Orders</span>
          <span class="info-value">{{
            trip.orders ? trip.orders.length : 0
          }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Delivered</span>
          <span class="info-value text-green">{{ ordersDelivered }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Pending</span>
          <span class="info-value">{{ ordersPending }}</span>
        </div>
        <a
          v-if="googleMapsUrl"
          :href="googleMapsUrl"
          target="_blank"
          class="btn-maps ml-auto"
        >
          🗺 Open in Google Maps
        </a>
      </div>

      <!-- Split layout: list + map -->
      <div class="split-layout">
        <!-- Left: order list -->
        <div class="orders-panel">
          <div class="orders-panel__header">
            <span class="orders-panel__title">Orders</span>
            <span class="orders-panel__count">{{ sortedOrders.length }}</span>
          </div>
          <div class="orders-panel__body">
            <div
              v-for="o in sortedOrders"
              :key="o.uuid"
              class="order-card"
              :class="{
                'order-card--delivered': o.status === 'delivered',
                'order-card--failed': o.status === 'failed',
                'order-card--active': highlightedOrderUuid === o.orderUuid
              }"
              @mouseenter="highlightMarker(o.orderUuid)"
              @mouseleave="highlightMarker(null)"
            >
              <div class="order-card__seq">
                <span class="seq-badge" :class="orderStatusClass(o.status)">
                  {{ +o.sequence || "—" }}
                </span>
              </div>
              <div class="order-card__body">
                <div class="order-card__number">{{ o.orderNumber }}</div>
                <div class="order-card__address">
                  {{ o.street1 }}{{ o.city ? ", " + o.city : "" }}
                </div>
                <div class="order-card__email">{{ o.customerEmail }}</div>
                <div v-if="!o.lat || !o.lng" class="order-card__geo-warning">
                  ⚠ Sin coordenadas — no se mostrará en el mapa
                </div>
                <div v-if="o.receivedBy" class="order-card__meta">
                  Received by: {{ o.receivedBy }}
                </div>
                <div v-if="o.trackingNumber" class="order-card__meta">
                  {{ o.trackingNumber }}
                </div>
              </div>
              <div class="order-card__right">
                <span class="status-badge" :class="orderStatusClass(o.status)">
                  {{ o.status }}
                </span>
                <button
                  v-if="trip.status === 'draft'"
                  class="link-red mt-1"
                  @click="removeOrder(o)"
                  title="Remove"
                >
                  ✕
                </button>
                <div
                  v-if="trip.status === 'in_progress' && o.status === 'pending'"
                  class="action-buttons"
                >
                  <button
                    class="btn-action btn-action--deliver"
                    @click="openDeliverDialog(o)"
                    title="Mark delivered"
                  >
                    ✓
                  </button>
                  <button
                    class="btn-action btn-action--fail"
                    @click="openFailDialog(o)"
                    title="Mark failed"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
            <div v-if="!sortedOrders.length" class="empty-state">
              No orders in this trip yet
            </div>
          </div>
        </div>

        <!-- Right: map -->
        <div class="map-panel">
          <div v-if="!mapsApiKey" class="map-placeholder">
            <div class="map-placeholder__icon">🗺</div>
            <div class="map-placeholder__text">
              Set <code>VUE_APP_GOOGLE_MAPS_KEY</code> to enable the map
            </div>
          </div>
          <div v-else-if="!hasGeocodedOrders" class="map-placeholder">
            <div class="map-placeholder__icon">📍</div>
            <div class="map-placeholder__text">
              No geocoded addresses yet.<br />Add orders to geocode them
              automatically.
            </div>
          </div>
          <div v-else ref="mapEl" class="map-container" />
        </div>
      </div>
    </template>

    <!-- Add orders modal -->
    <BaseModal v-model="showAddModal" @close="showAddModal = false">
      <div class="modal-add">
        <h3 class="text-lg font-semibold mb-3">Add Orders to Trip</h3>
        <div class="mb-3 flex gap-3 items-center">
          <input
            v-model="addSearch"
            class="flex-1 border rounded px-3 py-2 text-sm"
            placeholder="Search order / email / address..."
          />
          <label
            class="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap cursor-pointer"
          >
            <input
              type="checkbox"
              v-model="onlyLocalDelivery"
              @change="reloadAvailable"
            />
            Local delivery only
          </label>
        </div>
        <div v-if="loadingAvailable" class="empty-state">Loading...</div>
        <div v-else class="orders-list">
          <label v-for="o in filteredAvailable" :key="o.uuid" class="order-row">
            <input type="checkbox" :value="o.uuid" v-model="selectedOrders" />
            <div class="order-info">
              <span class="font-medium text-sm">{{ o.orderNumber }}</span>
              <span class="text-xs text-gray-500">{{ o.customerEmail }}</span>
              <span class="text-xs text-gray-400">
                {{ o.street1 }}{{ o.city ? ", " + o.city : "" }}
              </span>
            </div>
            <span class="text-xs text-gray-500 ml-auto">${{ o.total }}</span>
          </label>
          <div v-if="!filteredAvailable.length" class="empty-state">
            No available orders
          </div>
        </div>
        <div class="flex justify-between items-center mt-4">
          <span class="text-sm text-gray-500"
            >{{ selectedOrders.length }} selected</span
          >
          <div class="flex gap-2">
            <button
              class="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
              @click="showAddModal = false"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              @click="doAddOrders"
              :disabled="!selectedOrders.length || acting"
            >
              {{ acting ? "Adding..." : "Add Selected" }}
            </button>
          </div>
        </div>
      </div>
    </BaseModal>

    <!-- Deliver modal -->
    <BaseModal v-model="showDeliverModal" @close="showDeliverModal = false">
      <div class="modal-deliver">
        <h3 class="text-lg font-semibold mb-1">Confirm Delivery</h3>
        <div v-if="actingOrder" class="text-sm text-gray-500 mb-3">
          #{{ actingOrder.orderNumber }} · {{ actingOrder.street1 }}
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1"
            >Received by (optional)</label
          >
          <input
            v-model="deliverForm.receivedBy"
            class="w-full border rounded px-3 py-2 text-sm"
            placeholder="Customer name..."
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Notes (optional)</label>
          <textarea
            v-model="deliverForm.notes"
            class="w-full border rounded px-3 py-2 text-sm"
            rows="2"
            placeholder="Delivery notes..."
          />
        </div>
        <div v-if="capturingLocation" class="text-xs text-gray-400 mb-2">
          📍 Capturing location...
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
            @click="showDeliverModal = false"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            @click="confirmDeliver"
            :disabled="acting"
          >
            {{ acting ? "Saving..." : "Confirm Delivered" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <!-- Fail modal -->
    <BaseModal v-model="showFailModal" @close="showFailModal = false">
      <div class="modal-deliver">
        <h3 class="text-lg font-semibold mb-1">Mark as Failed</h3>
        <div v-if="actingOrder" class="text-sm text-gray-500 mb-3">
          #{{ actingOrder.orderNumber }} · {{ actingOrder.street1 }}
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Reason *</label>
          <textarea
            v-model="failForm.notes"
            class="w-full border rounded px-3 py-2 text-sm"
            rows="3"
            placeholder="e.g. Customer not home"
          />
        </div>
        <div v-if="capturingLocation" class="text-xs text-gray-400 mb-2">
          📍 Capturing location...
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
            @click="showFailModal = false"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            @click="confirmFail"
            :disabled="acting || !failForm.notes.trim()"
          >
            {{ acting ? "Saving..." : "Mark Failed" }}
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import {
  getTrip,
  getAvailableOrders,
  addOrdersToTrip,
  removeOrderFromTrip,
  startTrip,
  completeTrip,
  optimizeRoute,
  markDelivered,
  markFailed
} from "@/api/DeliveryService.js";

const STATUS_COLORS = {
  pending: { bg: "#f59e0b", text: "#fff" },
  delivered: { bg: "#16a34a", text: "#fff" },
  failed: { bg: "#dc2626", text: "#fff" }
};

export default {
  name: "DeliveryTripDetailPage",
  data() {
    return {
      trip: null,
      loading: false,
      acting: false,
      showAddModal: false,
      availableOrders: [],
      loadingAvailable: false,
      selectedOrders: [],
      addSearch: "",
      onlyLocalDelivery: true,
      highlightedOrderUuid: null,
      mapInstance: null,
      markers: {},
      showDeliverModal: false,
      showFailModal: false,
      actingOrder: null,
      deliverForm: { receivedBy: "", notes: "" },
      failForm: { notes: "" },
      capturingLocation: false
    };
  },
  computed: {
    mapsApiKey() {
      return process.env.VUE_APP_GOOGLE_MAPS_KEY || null;
    },
    department() {
      return this.trip ? this.trip.departamentoId || null : null;
    },
    sortedOrders() {
      if (!this.trip || !this.trip.orders) return [];
      return [...this.trip.orders].sort(
        (a, b) => (a.sequence || 0) - (b.sequence || 0)
      );
    },
    ordersDelivered() {
      if (!this.trip || !this.trip.orders) return 0;
      return this.trip.orders.filter(o => o.status === "delivered").length;
    },
    ordersPending() {
      if (!this.trip || !this.trip.orders) return 0;
      return this.trip.orders.filter(o => o.status === "pending").length;
    },
    hasGeocodedOrders() {
      if (!this.trip || !this.trip.orders) return false;
      return this.trip.orders.some(o => o.lat && o.lng);
    },
    relativeLabel() {
      if (!this.trip || !this.trip.createdAt) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(this.trip.createdAt);
      target.setHours(0, 0, 0, 0);
      const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
      if (diff === 0) return "Today";
      if (diff === -1) return "Yesterday";
      if (diff === 1) return "Tomorrow";
      return "";
    },
    filteredAvailable() {
      const q = this.addSearch.trim().toLowerCase();
      if (!q) return this.availableOrders;
      return this.availableOrders.filter(
        o =>
          (o.orderNumber || "").toLowerCase().includes(q) ||
          (o.customerEmail || "").toLowerCase().includes(q) ||
          (o.street1 || "").toLowerCase().includes(q) ||
          (o.city || "").toLowerCase().includes(q)
      );
    },
    googleMapsUrl() {
      if (!this.trip || !this.trip.orders) return null;
      const pending = this.sortedOrders.filter(
        o => o.status === "pending" && o.lat && o.lng
      );
      if (!pending.length) return null;
      const dest = pending[pending.length - 1];
      const waypoints = pending
        .slice(0, -1)
        .map(o => `${o.lat},${o.lng}`)
        .join("|");
      const base = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=driving`;
      return waypoints ? base + `&waypoints=${waypoints}` : base;
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.trip = await getTrip(this.$route.params.uuid);
        this.$nextTick(() => this.initMap());
      } finally {
        this.loading = false;
      }
    },

    // ── Map ──────────────────────────────────────────────────────────────────

    initMap() {
      if (!this.mapsApiKey || !this.hasGeocodedOrders) return;
      if (!this.$refs.mapEl) return;

      const load = () => {
        // eslint-disable-next-line no-undef
        const map = new google.maps.Map(this.$refs.mapEl, {
          zoom: 12,
          center: this._firstCoord(),
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
        this.mapInstance = map;
        this.markers = {};
        this.sortedOrders.forEach((o, idx) => {
          if (!o.lat || !o.lng) return;
          const colors = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
          // eslint-disable-next-line no-undef
          const marker = new google.maps.Marker({
            position: { lat: parseFloat(o.lat), lng: parseFloat(o.lng) },
            map,
            label: {
              text: String(o.sequence || idx + 1),
              color: colors.text,
              fontWeight: "bold",
              fontSize: "12px"
            },
            icon: {
              // eslint-disable-next-line no-undef
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: colors.bg,
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
              scale: 16
            },
            title: `${o.orderNumber} — ${o.street1}`
          });
          // eslint-disable-next-line no-undef
          const infoWindow = new google.maps.InfoWindow({
            content: `<div style="font-size:12px;max-width:200px">
              <strong>#${o.orderNumber}</strong><br>
              ${o.street1}${o.city ? ", " + o.city : ""}<br>
              <em>${o.customerEmail}</em>
            </div>`
          });
          marker.addListener("click", () => infoWindow.open(map, marker));
          this.markers[o.orderUuid] = marker;
        });
        this._fitBounds(map);
      };

      if (window.google && window.google.maps) {
        load();
      } else {
        const scriptId = "google-maps-script";
        if (!document.getElementById(scriptId)) {
          const script = document.createElement("script");
          script.id = scriptId;
          script.src = `https://maps.googleapis.com/maps/api/js?key=${this.mapsApiKey}`;
          script.async = true;
          script.defer = true;
          script.onload = load;
          document.head.appendChild(script);
        } else {
          // Script already loading — poll
          const wait = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(wait);
              load();
            }
          }, 100);
        }
      }
    },

    destroyMap() {
      this.mapInstance = null;
      this.markers = {};
    },

    _firstCoord() {
      const o = this.sortedOrders.find(o => o.lat && o.lng);
      return o
        ? { lat: parseFloat(o.lat), lng: parseFloat(o.lng) }
        : { lat: 0, lng: 0 };
    },

    _fitBounds(map) {
      const coords = this.sortedOrders.filter(o => o.lat && o.lng);
      if (!coords.length) return;
      if (coords.length === 1) return;
      // eslint-disable-next-line no-undef
      const bounds = new google.maps.LatLngBounds();
      coords.forEach(o =>
        bounds.extend({ lat: parseFloat(o.lat), lng: parseFloat(o.lng) })
      );
      map.fitBounds(bounds, 60);
    },

    highlightMarker(orderUuid) {
      this.highlightedOrderUuid = orderUuid;
      if (!this.mapInstance) return;
      Object.entries(this.markers).forEach(([uuid, marker]) => {
        const isActive = uuid === orderUuid;
        marker.setZIndex(isActive ? 100 : 1);
        const order = this.trip.orders.find(o => o.orderUuid === uuid);
        const colors = STATUS_COLORS[(order && order.status) || "pending"];
        marker.setIcon({
          // eslint-disable-next-line no-undef
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: colors.bg,
          fillOpacity: 1,
          strokeColor: isActive ? "#1d4ed8" : "#fff",
          strokeWeight: isActive ? 3 : 2,
          scale: isActive ? 20 : 16
        });
      });
    },

    // ── Actions ──────────────────────────────────────────────────────────────

    async openAddOrders() {
      this.showAddModal = true;
      this.selectedOrders = [];
      this.addSearch = "";
      this.onlyLocalDelivery = true;
      await this.reloadAvailable();
    },
    async reloadAvailable() {
      this.loadingAvailable = true;
      try {
        const params = {};
        if (this.department) params.department = this.department;
        if (this.trip && this.trip.warehouseId)
          params.warehouse_id = this.trip.warehouseId;
        if (!this.onlyLocalDelivery) params.shipping_preference = "all";
        this.availableOrders = await getAvailableOrders(params);
      } finally {
        this.loadingAvailable = false;
      }
    },
    async doAddOrders() {
      if (!this.selectedOrders.length) return;
      this.acting = true;
      try {
        await addOrdersToTrip(this.trip.uuid, this.selectedOrders);
        this.showAddModal = false;
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || "Error adding orders");
      } finally {
        this.acting = false;
      }
    },
    async removeOrder(order) {
      if (!confirm(`Remove order ${order.orderNumber} from trip?`)) return;
      try {
        await removeOrderFromTrip(this.trip.uuid, order.orderUuid);
        await this.load();
      } catch {
        alert("Error removing order");
      }
    },
    async _captureLocation() {
      if (!navigator.geolocation) return null;
      this.capturingLocation = true;
      try {
        return await new Promise(resolve => {
          navigator.geolocation.getCurrentPosition(
            pos =>
              resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { timeout: 5000, enableHighAccuracy: false }
          );
        });
      } finally {
        this.capturingLocation = false;
      }
    },
    openDeliverDialog(order) {
      this.actingOrder = order;
      this.deliverForm = { receivedBy: "", notes: "" };
      this.showDeliverModal = true;
    },
    openFailDialog(order) {
      this.actingOrder = order;
      this.failForm = { notes: "" };
      this.showFailModal = true;
    },
    async confirmDeliver() {
      if (!this.actingOrder) return;
      this.acting = true;
      try {
        const loc = await this._captureLocation();
        await markDelivered(this.trip.uuid, this.actingOrder.orderUuid, {
          received_by: this.deliverForm.receivedBy || null,
          delivery_notes: this.deliverForm.notes || null,
          lat: loc ? loc.lat : null,
          lng: loc ? loc.lng : null
        });
        this.showDeliverModal = false;
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || "Error confirming delivery");
      } finally {
        this.acting = false;
      }
    },
    async confirmFail() {
      if (!this.actingOrder || !this.failForm.notes.trim()) return;
      this.acting = true;
      try {
        const loc = await this._captureLocation();
        await markFailed(this.trip.uuid, this.actingOrder.orderUuid, {
          delivery_notes: this.failForm.notes,
          lat: loc ? loc.lat : null,
          lng: loc ? loc.lng : null
        });
        this.showFailModal = false;
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || "Error marking failed");
      } finally {
        this.acting = false;
      }
    },
    async confirmStart() {
      if (!confirm("Start trip? This will notify customers via email.")) return;
      this.acting = true;
      try {
        await startTrip(this.trip.uuid);
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || "Error starting trip");
      } finally {
        this.acting = false;
      }
    },
    async confirmOptimize() {
      this.acting = true;
      try {
        await optimizeRoute(this.trip.uuid);
        await this.load();
      } catch {
        alert("Error optimizing route");
      } finally {
        this.acting = false;
      }
    },
    async confirmComplete() {
      if (!confirm("Complete trip? Undelivered orders will return to pending."))
        return;
      this.acting = true;
      try {
        await completeTrip(this.trip.uuid);
        await this.load();
      } catch {
        alert("Error completing trip");
      } finally {
        this.acting = false;
      }
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    statusLabel(s) {
      return (
        {
          draft: "Draft",
          in_progress: "In Progress",
          completed: "Completed",
          cancelled: "Cancelled"
        }[s] || s
      );
    },
    statusClass(s) {
      return {
        "status--draft": s === "draft",
        "status--progress": s === "in_progress",
        "status--completed": s === "completed",
        "status--cancelled": s === "cancelled"
      };
    },
    orderStatusClass(s) {
      return {
        "status--pending": s === "pending",
        "status--delivered": s === "delivered",
        "status--failed": s === "failed"
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-page {
  width: 100%;
  height: 100%;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.header-center {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.page-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.relative-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
}

/* Info bar */
.info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.875rem 1.25rem;
  flex-shrink: 0;
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.info-label {
  font-size: 0.7rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.info-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  &.text-green {
    color: #15803d;
  }
}

/* Split layout */
.split-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1rem;
  flex: 1;
  min-height: 0;
}

/* Order list panel */
.orders-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  min-height: 0;
}
.orders-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.orders-panel__title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}
.orders-panel__count {
  background: #e5e7eb;
  color: #374151;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
}
.orders-panel__body {
  overflow-y: auto;
  flex: 1;
}

/* Order card */
.order-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: default;
  transition: background 0.1s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f9fafb;
  }
  &--delivered {
    background: #f0fdf4;
  }
  &--failed {
    background: #fef2f2;
  }
  &--active {
    background: #eff6ff !important;
    outline: 2px solid #3b82f6;
  }
}
.order-card__seq {
  flex-shrink: 0;
  padding-top: 2px;
}
.seq-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  background: #9ca3af;
  &.status--pending {
    background: #f59e0b;
  }
  &.status--delivered {
    background: #16a34a;
  }
  &.status--failed {
    background: #dc2626;
  }
}
.order-card__body {
  flex: 1;
  min-width: 0;
}
.order-card__number {
  font-size: 0.82rem;
  font-weight: 600;
  color: #111827;
}
.order-card__address {
  font-size: 0.78rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.order-card__email {
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.order-card__meta {
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 0.15rem;
}
.order-card__geo-warning {
  font-size: 0.72rem;
  color: #b45309;
  margin-top: 0.15rem;
}
.order-card__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

/* Map panel */
.map-panel {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  position: relative;
  min-height: 400px;
}
.map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
.map-placeholder {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  gap: 0.75rem;
}
.map-placeholder__icon {
  font-size: 2.5rem;
  opacity: 0.4;
}
.map-placeholder__text {
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
  code {
    background: #e5e7eb;
    padding: 0.1rem 0.35rem;
    border-radius: 0.25rem;
    font-family: monospace;
  }
}

/* Buttons */
.btn-back {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  color: #374151;
  &:hover {
    background: #f3f4f6;
  }
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  padding: 0.45rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #1d4ed8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.btn-success {
  background: #16a34a;
  color: #fff;
  padding: 0.45rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #15803d;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.btn-warning {
  background: #d97706;
  color: #fff;
  padding: 0.45rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #b45309;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.btn-danger {
  background: #dc2626;
  color: #fff;
  padding: 0.45rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #b91c1c;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.btn-maps {
  background: #fff;
  color: #2563eb;
  padding: 0.4rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  border: 1.5px solid #2563eb;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    background: #eff6ff;
  }
}

/* Status badges */
.status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  &.status--draft {
    background: #f3f4f6;
    color: #6b7280;
  }
  &.status--progress {
    background: #fef3c7;
    color: #b45309;
  }
  &.status--completed {
    background: #dcfce7;
    color: #15803d;
  }
  &.status--cancelled {
    background: #fee2e2;
    color: #b91c1c;
  }
  &.status--pending {
    background: #fef3c7;
    color: #b45309;
  }
  &.status--delivered {
    background: #dcfce7;
    color: #15803d;
  }
  &.status--failed {
    background: #fee2e2;
    color: #b91c1c;
  }
}
.link-red {
  color: #dc2626;
  font-size: 0.8rem;
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.empty-state {
  padding: 2.5rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Action buttons (deliver/fail per order) */
.action-buttons {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.35rem;
}
.btn-action {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  &--deliver {
    background: #16a34a;
    &:hover {
      background: #15803d;
    }
  }
  &--fail {
    background: #dc2626;
    &:hover {
      background: #b91c1c;
    }
  }
}

/* Deliver / Fail modals */
.modal-deliver {
  min-width: 380px;
  max-width: 460px;
  padding: 1.5rem;
}

/* Add orders modal */
.modal-add {
  min-width: 480px;
  max-width: 600px;
  padding: 1.5rem;
}
.orders-list {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}
.order-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f9fafb;
  }
  input[type="checkbox"] {
    cursor: pointer;
    flex-shrink: 0;
  }
}
.order-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}

/* Responsive: stack on narrow screens */
@media (max-width: 768px) {
  .split-layout {
    grid-template-columns: 1fr;
  }
  .map-panel {
    min-height: 300px;
  }
}
</style>
